import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, MessageSquareText, Clock, CheckCircle2, Loader2, Mail, Phone, School, Building2, Coins, Plus } from "lucide-react";

const STATUS_OPTIONS = ["pending", "in_progress", "resolved"];

export default function AdminDashboard() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesDraft, setNotesDraft] = useState({});
  const [schools, setSchools] = useState([]);
  const [newSchool, setNewSchool] = useState({ name: "", initial_credits: 0 });
  const [grantDraft, setGrantDraft] = useState({});

  const load = useCallback(async () => {
    try {
      const [s, r, q, sc] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/rep-requests"),
        api.get("/admin/questions"),
        api.get("/admin/schools"),
      ]);
      setStats(s.data);
      setRequests(r.data);
      setQuestions(q.data);
      setSchools(sc.data);
      const drafts = {};
      r.data.forEach((req) => (drafts[req.id] = req.admin_notes || ""));
      setNotesDraft(drafts);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateRequest = async (id, patch) => {
    try {
      await api.patch(`/rep-requests/${id}`, patch);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const createSchool = async (e) => {
    e.preventDefault();
    try {
      await api.post("/schools", { name: newSchool.name, initial_credits: Number(newSchool.initial_credits) || 0 });
      toast.success("School created");
      setNewSchool({ name: "", initial_credits: 0 });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const grantCredits = async (schoolId) => {
    const amount = Number(grantDraft[schoolId]);
    if (!amount || amount < 1) return;
    try {
      await api.post(`/admin/schools/${schoolId}/grant`, { credits: amount });
      toast.success(`Granted ${amount} credits`);
      setGrantDraft((d) => ({ ...d, [schoolId]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const statCards = stats
    ? [
        { icon: Users, label: t("admin_stat_parents"), value: stats.total_parents, cls: "text-secondary bg-secondary/15" },
        { icon: MessageSquareText, label: t("admin_stat_questions"), value: stats.total_questions, cls: "text-primary bg-primary/10" },
        { icon: Clock, label: t("admin_stat_pending"), value: stats.pending_requests, cls: "text-amber-600 bg-amber-100" },
        { icon: CheckCircle2, label: t("admin_stat_resolved"), value: stats.resolved_requests, cls: "text-accent bg-accent/10" },
        { icon: Building2, label: t("nav_schools"), value: stats.total_schools, cls: "text-accent bg-accent/10" },
        { icon: Coins, label: t("school_credits"), value: stats.total_credits, cls: "text-primary bg-primary/10" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl" data-testid="admin-title">
          {t("admin_title")}
        </h1>
        <p className="mt-2 text-base text-muted-foreground">{t("admin_subtitle")}</p>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
              {statCards.map((s, i) => (
                <div key={i} className="rounded-3xl border border-black/5 bg-card p-6 shadow-sm">
                  <span className={`grid h-11 w-11 place-items-center rounded-2xl ${s.cls}`}>
                    <s.icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <p className="mt-4 font-heading text-3xl font-semibold text-foreground">{s.value}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>

            <Tabs defaultValue="requests" className="mt-10">
              <TabsList className="rounded-full bg-surfaceAlt p-1">
                <TabsTrigger data-testid="tab-requests" value="requests" className="rounded-full data-[state=active]:bg-card data-[state=active]:text-primary">
                  {t("admin_tab_requests")}
                </TabsTrigger>
                <TabsTrigger data-testid="tab-questions" value="questions" className="rounded-full data-[state=active]:bg-card data-[state=active]:text-primary">
                  {t("admin_tab_questions")}
                </TabsTrigger>
                <TabsTrigger data-testid="tab-schools" value="schools" className="rounded-full data-[state=active]:bg-card data-[state=active]:text-primary">
                  {t("admin_tab_schools")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="requests" className="mt-6 space-y-4">
                {requests.length === 0 ? (
                  <p className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
                    {t("dash_no_requests")}
                  </p>
                ) : (
                  requests.map((r) => (
                    <div key={r.id} data-testid={`admin-request-${r.id}`} className="rounded-3xl border border-black/5 bg-card p-6 shadow-sm">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="font-heading text-lg font-semibold text-foreground">{r.topic}</h3>
                          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {r.parent_name} · {r.parent_email}</span>
                            {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {r.phone}</span>}
                            {r.student_school && <span className="inline-flex items-center gap-1"><School className="h-3.5 w-3.5" /> {r.student_school}</span>}
                            <span className="rounded-full bg-surfaceAlt px-2 py-0.5 font-semibold uppercase">{r.preferred_language === "es" ? "Español" : "English"}</span>
                          </div>
                        </div>
                        <select
                          data-testid={`status-select-${r.id}`}
                          value={r.status}
                          onChange={(e) => updateRequest(r.id, { status: e.target.value })}
                          className="rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground outline-none focus:border-primary"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{t(`status_${s}`)}</option>
                          ))}
                        </select>
                      </div>
                      <p className="mt-3 rounded-2xl bg-surfaceAlt px-4 py-3 text-sm leading-relaxed text-foreground">{r.message}</p>
                      <div className="mt-4">
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("admin_notes")}</label>
                        <div className="flex gap-2">
                          <input
                            data-testid={`notes-input-${r.id}`}
                            value={notesDraft[r.id] ?? ""}
                            onChange={(e) => setNotesDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                            placeholder={t("admin_notes_ph")}
                            className="flex-1 rounded-2xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                          />
                          <Button
                            data-testid={`notes-save-${r.id}`}
                            onClick={() => updateRequest(r.id, { admin_notes: notesDraft[r.id] ?? "" })}
                            className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary-hover"
                          >
                            {t("admin_save")}
                          </Button>
                        </div>
                      </div>
                      <p className="mt-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
                    </div>
                  ))
                )}
              </TabsContent>

              <TabsContent value="questions" className="mt-6 space-y-3">
                {questions.length === 0 ? (
                  <p data-testid="no-questions" className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
                    {t("admin_no_questions")}
                  </p>
                ) : (
                  <div data-testid="questions-list" className="space-y-3">
                    {questions.map((q, i) => (
                      <div key={i} className="flex items-start justify-between gap-4 rounded-2xl border border-black/5 bg-card px-5 py-4 shadow-sm">
                        <p className="text-sm leading-relaxed text-foreground">{q.content}</p>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span className="rounded-full bg-surfaceAlt px-2 py-0.5 text-xs font-semibold uppercase text-muted-foreground">{q.language === "es" ? "ES" : "EN"}</span>
                          <span className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="schools" className="mt-6 space-y-5">
                <form onSubmit={createSchool} className="flex flex-col gap-3 rounded-3xl border border-black/5 bg-card p-6 shadow-sm sm:flex-row sm:items-end" data-testid="create-school-form">
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("school_name")}</label>
                    <input
                      data-testid="new-school-name"
                      required
                      value={newSchool.name}
                      onChange={(e) => setNewSchool((s) => ({ ...s, name: e.target.value }))}
                      className="w-full rounded-2xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="w-full sm:w-40">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("initial_credits")}</label>
                    <input
                      data-testid="new-school-credits"
                      type="number"
                      min="0"
                      value={newSchool.initial_credits}
                      onChange={(e) => setNewSchool((s) => ({ ...s, initial_credits: e.target.value }))}
                      className="w-full rounded-2xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <Button data-testid="create-school-btn" type="submit" className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary-hover">
                    <Plus className="mr-1.5 h-4 w-4" /> {t("create_school")}
                  </Button>
                </form>

                {schools.length === 0 ? (
                  <p data-testid="no-schools" className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
                    {t("no_schools")}
                  </p>
                ) : (
                  <div className="space-y-4" data-testid="schools-list">
                    {schools.map((sc) => (
                      <div key={sc.id} data-testid={`school-card-${sc.id}`} className="flex flex-col gap-4 rounded-3xl border border-black/5 bg-card p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-4">
                          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-accent/10 text-accent">
                            <Building2 className="h-6 w-6" strokeWidth={1.75} />
                          </span>
                          <div>
                            <h3 className="font-heading text-lg font-semibold text-foreground">{sc.name}</h3>
                            <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                              <span className="inline-flex items-center gap-1"><Coins className="h-3.5 w-3.5 text-primary" /> {sc.credit_balance} {t("credits_word")}</span>
                              <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {sc.num_parents}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <input
                            data-testid={`grant-input-${sc.id}`}
                            type="number"
                            min="1"
                            placeholder={t("grant_credits_ph")}
                            value={grantDraft[sc.id] ?? ""}
                            onChange={(e) => setGrantDraft((d) => ({ ...d, [sc.id]: e.target.value }))}
                            className="w-28 rounded-2xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                          />
                          <Button
                            data-testid={`grant-btn-${sc.id}`}
                            onClick={() => grantCredits(sc.id)}
                            variant="outline"
                            className="rounded-full border-2 border-primary font-semibold text-primary hover:bg-primary hover:text-primary-foreground"
                          >
                            {t("grant")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
